import React from "react";
import { Droplet, LayoutGrid, Sun, Grid3x3, Building2, Home } from "lucide-react";

const SERVICES = [
  {
    icon: Droplet,
    title: "Exterior Glass Cleaning",
    desc: "Streak-free exterior glass using our water-fed pole or hand-cleaning — whatever the job calls for. Interior available as add-on.",
    span: "lg:col-span-2 lg:row-span-2",
    accent: "from-[#0F4C81] to-[#00B4D8]",
  },
  {
    icon: LayoutGrid,
    title: "Frames, Sills & Tracks",
    desc: "Detailed scrubbing of every frame, sill and track — no dust or grime left behind.",
    span: "lg:col-span-2",
    accent: "from-[#00B4D8] to-[#90E0EF]",
  },
  {
    icon: Grid3x3,
    title: "Screen Cleaning",
    desc: "Pulled, washed, and reinstalled. Priced per screen as an add-on — just let us know your count.",
    span: "lg:col-span-1",
    accent: "from-[#0F4C81] to-[#0B3A66]",
  },
  {
    icon: Sun,
    title: "Solar Panel Cleaning",
    desc: "Restore efficiency with safe, deionized rinses that lift dust without scratching.",
    span: "lg:col-span-1",
    accent: "from-[#FFB703] to-[#FB8500]",
  },
  {
    icon: Home,
    title: "Residential",
    desc: "Single-story cottages to multi-story estates across greater Baton Rouge.",
    span: "lg:col-span-1",
    accent: "from-[#0F4C81] to-[#00B4D8]",
  },
  {
    icon: Building2,
    title: "Commercial",
    desc: "Storefronts, offices, and retail glass kept presentable for your customers.",
    span: "lg:col-span-1",
    accent: "from-[#0B3A66] to-[#0F4C81]",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#00B4D8] font-bold">
            What we clean
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-extrabold text-[#0B2542] leading-tight">
            Everything that lets the light in.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            From single-pane cottages to commercial storefronts, we handle every detail of
            window care with the right method for the job.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-4 lg:gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isHero = i === 0;
            return (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className={`group relative rounded-3xl overflow-hidden border border-blue-100 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#00B4D8]/50 hover:shadow-[0_18px_40px_rgba(15,76,129,0.10)] ${s.span}`}
              >
                {isHero && (
                  <>
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(135deg, #0F4C81 0%, #00B4D8 100%)",
                      }}
                    />
                    <div
                      aria-hidden
                      className="absolute -top-20 -right-16 h-72 w-72 rounded-full opacity-50"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(144,224,239,0.55), rgba(144,224,239,0) 65%)",
                      }}
                    />
                    <div
                      aria-hidden
                      className="absolute -bottom-16 -left-12 h-64 w-64 rounded-full opacity-40"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(255,255,255,0.4), rgba(255,255,255,0) 65%)",
                      }}
                    />
                  </>
                )}

                <div
                  className={`relative p-7 lg:p-8 h-full flex flex-col justify-end min-h-[200px] ${
                    isHero ? "min-h-[420px] text-white" : ""
                  }`}
                >
                  <div
                    className={`h-11 w-11 rounded-xl flex items-center justify-center mb-5 ${
                      isHero
                        ? "bg-white/20 ring-1 ring-white/30 text-white backdrop-blur-sm"
                        : `bg-gradient-to-br ${s.accent} text-white shadow-[0_8px_20px_rgba(0,180,216,0.25)]`
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3
                    className={`font-[Outfit] font-bold leading-tight ${
                      isHero ? "text-3xl sm:text-4xl text-white" : "text-xl text-[#0B2542]"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`mt-2 text-sm ${
                      isHero ? "text-white/85 max-w-md" : "text-slate-600"
                    }`}
                  >
                    {s.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
